import { useToastr } from '@farmshare/ui-components';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import {
  useMyChatRoomUpdatesQuery,
  useMyInAppNotificationsQuery,
  useServerTimeQuery,
} from '../../../lib/graphql';
import { userState } from 'state';

/** How long a live toast sits on screen. Longer than the 3s default: these
 *  arrive unprompted rather than in response to something you just did, so the
 *  reader needs time to notice one before reading it. */
const TOAST_DELAY_MS = 8_000;
// Every toast here is `removeOnNextPush`: several alerts landing in one poll
// (a batch of status changes, a burst of messages) would otherwise stack a
// card each and cover the screen on a phone. The next one replaces the last;
// the bell and the chat list still hold all of them.

/** Chat delta-poll cadence, matching the chat page's own. */
const CHAT_POLL_MS = 15_000;

/** In-app notification poll, matching the bell's — same query and variables,
 *  so Apollo serves both from one request rather than polling twice. */
const NOTIFICATION_POLL_MS = 30_000;
const NOTIFICATION_LIMIT = 20;

/**
 * Whether the tab is in the foreground, so polling can stop while it isn't —
 * the same guard the notification bell uses.
 * @returns True while the document is visible.
 */
function useTabVisible(): boolean {
  const [visible, setVisible] = useState(
    typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/**
 * Surfaces new in-app notifications and new chat messages as toasts while the
 * user is anywhere in the portal.
 *
 * Renders nothing. Mounted once per shell (producer and buyer) rather than per
 * page, so a notification arriving while you're deep in a job still reaches
 * you.
 *
 * Two sources, because there is only one for notifications. `myInAppNotifications`
 * already polls for the bell and carries a title, body and CTA. Chat messages
 * write NO in-app row — their only notification channel is an email fallback —
 * so the chat half rides the same delta poll the chat page uses
 * (`myChatRoomUpdates`), which returns changed rooms with their last message.
 *
 * Both halves seed themselves silently on their first response: arriving at a
 * page with nine unread notifications should not fire nine toasts. Only things
 * that land WHILE you are here get announced.
 */
export function PortalLiveToasts() {
  const user = useRecoilValue(userState);
  const { push } = useToastr();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isTabVisible = useTabVisible();

  // A stable server-clock cursor for the chat delta. Seeding from the browser
  // clock risks a skewed client asking for `updatedAt > <the future>` and
  // silently never seeing a message again.
  const { data: serverTimeData } = useServerTimeQuery({ skip: !user });
  const since = serverTimeData?.serverTime
    ? new Date(serverTimeData.serverTime).toISOString()
    : null;

  const { data: notificationData } = useMyInAppNotificationsQuery({
    skip: !user,
    fetchPolicy: 'cache-and-network',
    pollInterval: isTabVisible ? NOTIFICATION_POLL_MS : 0,
    variables: { limit: NOTIFICATION_LIMIT },
  });

  const { data: chatData } = useMyChatRoomUpdatesQuery({
    skip: !user || !since,
    fetchPolicy: 'network-only',
    pollInterval: isTabVisible ? CHAT_POLL_MS : 0,
    variables: { since },
  });

  // Ids already announced (or present before we arrived). Refs, not state:
  // updating them must not re-render, and they are read inside the effects
  // that write them.
  const seenNotificationIds = useRef<Set<string> | null>(null);
  const seenChatKeys = useRef<Set<string> | null>(null);

  // The chat page shows its own messages arriving; a toast over the top of the
  // conversation you're reading is noise.
  const onChatPage = pathname.startsWith('/chat');
  const onChatPageRef = useRef(onChatPage);
  onChatPageRef.current = onChatPage;

  useEffect(() => {
    const items = notificationData?.myInAppNotifications?.items;
    if (!items) return;

    const ids = items
      .map((item) => item?._id)
      .filter((id): id is string => Boolean(id));

    // First response: remember what was already there, announce nothing.
    if (seenNotificationIds.current === null) {
      seenNotificationIds.current = new Set(ids);
      return;
    }

    for (const item of items) {
      if (!item?._id || seenNotificationIds.current.has(item._id)) continue;
      seenNotificationIds.current.add(item._id);

      // Already-seen rows can still be new TO US (another tab, or the digest
      // marking them). Only announce genuinely unseen ones.
      if (item.seenAt) continue;

      const ctaUrl = item.ctaUrl;
      push({
        id: `notification-${item._id}`,
        title: item.title,
        body: item.body ?? '',
        severity: 'info',
        delay: TOAST_DELAY_MS,
        removeOnNextPush: true,
        ...(ctaUrl
          ? {
              action: {
                label: 'View',
                onClick: () => {
                  // Absolute URLs (a hosted invoice, say) leave the app;
                  // in-app paths route without a reload.
                  if (/^https?:\/\//i.test(ctaUrl))
                    window.location.href = ctaUrl;
                  else navigate(ctaUrl);
                },
              },
            }
          : {}),
      });
    }
  }, [notificationData, push, navigate]);

  useEffect(() => {
    const rooms = chatData?.myChatRoomUpdates;
    if (!rooms) return;

    // The delta poll returns every room changed since the (fixed) cursor, so
    // the same room comes back on every tick. Key on the room plus its last
    // update, which changes exactly when a new message lands.
    const keyFor = (roomId: string, updatedAt: unknown) =>
      `${roomId}:${String(updatedAt ?? '')}`;

    if (seenChatKeys.current === null) {
      seenChatKeys.current = new Set(
        rooms
          .filter((room) => room?._id)
          .map((room) => keyFor(String(room?._id), room?.updatedAt)),
      );
      return;
    }

    for (const room of rooms) {
      if (!room?._id) continue;
      const key = keyFor(String(room._id), room.updatedAt);
      if (seenChatKeys.current.has(key)) continue;
      seenChatKeys.current.add(key);

      // Nothing unread, or it's our own message echoing back.
      if (!room.unreadMessagesCount) continue;
      const message = room.lastMessage;
      if (!message?.content) continue;
      if (message.sender?._id && message.sender._id === user?._id) continue;
      if (onChatPageRef.current) continue;

      push({
        id: `chat-${key}`,
        title: `New message from ${chatRoomName(room)}`,
        body: message.content,
        severity: 'info',
        delay: TOAST_DELAY_MS,
        removeOnNextPush: true,
        action: {
          label: 'Open chat',
          onClick: () => navigate('/chat'),
        },
      });
    }
  }, [chatData, push, navigate, user?._id]);

  return null;
}

/**
 * Who a room is with, from whichever side is populated: a vendor room carries
 * the shop, a direct buyer↔producer room carries the producer.
 * @param room - A room from the delta poll.
 * @returns A display name, or a neutral fallback.
 */
function chatRoomName(room: {
  vendor?: { shop_name?: string | null } | null;
  producer?: {
    company?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
}): string {
  if (room.vendor?.shop_name) return room.vendor.shop_name;
  const producer = room.producer;
  if (producer?.company) return producer.company;
  const name = [producer?.first_name, producer?.last_name]
    .filter(Boolean)
    .join(' ');
  return name || 'Farmshare';
}
